import React, { useMemo, useState } from "react";
import { useI18n, StatusBadge, GsdReadableBlock } from "@/coquille-bridge";
import { DataTable } from "@/components/DataTable.jsx";
import { EntityCrudPanel, demoFlag } from "@/components/admin/EntityCrudPanel.jsx";
import { EntityStatusBadge } from "@/components/DocumentVisibilityBadge.jsx";
import { DemoBadge } from "@/components/DemoBadge.jsx";
import { useDataStore } from "@/components/admin/useDataStore.js";
import { useAuth } from "@/context/AuthContext.jsx";
import { getDemoContextForAccount } from "@/data/dojoOsOrgModel.js";
import { upsertPresence } from "@/data/dataStore.js";
import { notifyDataUpdate } from "@/components/admin/useDataStore.js";
import { DEMO_ATTENDANCE_DATE } from "@/data/demoScheduleData.js";
import {
  CLASS_CATEGORIES,
  EVENT_TYPES,
  ATTENDANCE_STATUSES,
  PHASE_LABELS,
  WEEKDAYS,
  labelFromMap,
} from "@/utils/scheduleLabels.js";
import { useKarateUi } from "@/hooks/useKarateUi.js";
import { seedResponsibleId } from "@/data/phase1InitialSeed046.js";

const SENSEI_INSTRUCTOR_ID = "demo-sensei-placeholder";
const SEMPAI_INSTRUCTOR_ID = seedResponsibleId(1);

function useLocale() {
  const { lang } = useI18n();
  return lang === "en" ? "en" : "fr";
}

export function PhaseBadge({ phase, testId = "phase-badge" }) {
  const locale = useLocale();
  const tone = phase === "phase1" ? "blue" : phase === "phase2" ? "yellow" : "neutral";
  return (
    <StatusBadge
      label={labelFromMap(PHASE_LABELS, phase, locale)}
      tone={tone}
      testId={`${testId}-${phase}`}
    />
  );
}

export function useScheduleScope() {
  const { username, role, activeRoleUi } = useAuth();
  const ctx = getDemoContextForAccount(username);
  const effectiveRole = activeRoleUi || role;

  return useMemo(() => {
    if (role === "hanshi" || role === "admin" || effectiveRole === "hanshi") {
      return { mode: "admin" };
    }
    if (effectiveRole === "sensei" || role === "sensei") {
      return {
        mode: "sensei",
        dojoId: ctx?.dojoId,
        instructeurId: SENSEI_INSTRUCTOR_ID,
      };
    }
    if (effectiveRole === "assistant" || role === "assistant") {
      return {
        mode: "sempai",
        dojoId: ctx?.dojoId,
        assistantId: SEMPAI_INSTRUCTOR_ID,
        readOnly: false,
      };
    }
    if (effectiveRole === "parent" || role === "parent") {
      return { mode: "parent", childIds: ctx?.childStudentIds || [] };
    }
    if (effectiveRole === "student" || role === "student") {
      return {
        mode: "student",
        dojoId: ctx?.dojoId,
        studentId: ctx?.studentId,
      };
    }
    return { mode: "guest" };
  }, [role, effectiveRole, ctx]);
}

export function ClassesListPanel({
  scope: scopeProp,
  allowCrud = false,
  testId = "karate-classes-panel",
}) {
  const locale = useLocale();
  const { localizeDemo } = useKarateUi();
  const store = useDataStore();
  const scope = scopeProp || useScheduleScope();
  const labels = locale === "en"
    ? { empty: "No classes found.", notice: "Official schedules pending — [DEMO] data." }
    : { empty: "Aucun cours trouvé.", notice: "Horaires officiels en attente — données [DÉMO]." };

  const rows = useMemo(() => {
    let list = store.classes.filter((c) => c.statut !== "inactif");
    if (scope.mode === "sensei") {
      list = list.filter((c) => c.instructeur_id === SENSEI_INSTRUCTOR_ID);
    } else if (scope.mode === "sempai") {
      list = list.filter((c) => c.assistant_id === SEMPAI_INSTRUCTOR_ID);
    } else if (scope.mode === "student" && scope.studentId) {
      const student = store.etudiants.find((e) => e.id === scope.studentId);
      list = list.filter(
        (c) =>
          c.id === student?.class_id ||
          (student && c.dojo_id === student.dojo_id && c.groupe === student.groupe),
      );
    } else if (scope.mode === "parent" && scope.childIds?.length) {
      const children = store.etudiants.filter((e) => scope.childIds.includes(e.id));
      const classIds = new Set(children.map((c) => c.class_id).filter(Boolean));
      const groupes = new Set(children.map((c) => `${c.dojo_id}:${c.groupe}`));
      list = list.filter(
        (c) => classIds.has(c.id) || groupes.has(`${c.dojo_id}:${c.groupe}`),
      );
    }
    return list;
  }, [store.classes, store.etudiants, scope]);

  const dojoOptions = store.dojos.map((d) => ({ value: d.id, label: localizeDemo(d.nom) }));
  const instructeurOptions = store.instructeurs.map((i) => ({ value: i.id, label: localizeDemo(i.nom) }));

  const columns = [
    {
      key: "nom",
      label: locale === "en" ? "Name" : "Nom",
      render: (r) => (
        <>
          {localizeDemo(r.nom)} {demoFlag(r)}
        </>
      ),
    },
    {
      key: "dojo_id",
      label: "Dojo",
      render: (r) => localizeDemo(store.dojos.find((d) => d.id === r.dojo_id)?.nom ?? r.dojo_id),
    },
    { key: "groupe", label: locale === "en" ? "Group" : "Groupe", render: (r) => localizeDemo(r.groupe) },
    {
      key: "categorie",
      label: locale === "en" ? "Category" : "Catégorie",
      render: (r) => labelFromMap(CLASS_CATEGORIES, r.categorie, locale),
    },
    {
      key: "jour",
      label: locale === "en" ? "Day" : "Jour",
      render: (r) => labelFromMap(WEEKDAYS, r.jour, locale),
    },
    {
      key: "heure_debut",
      label: locale === "en" ? "Schedule" : "Horaire",
      render: (r) => `${r.heure_debut} – ${r.heure_fin}`,
    },
    {
      key: "instructeur_id",
      label: "Sensei",
      render: (r) => localizeDemo(store.instructeurs.find((i) => i.id === r.instructeur_id)?.nom ?? "—"),
    },
    {
      key: "assistant_id",
      label: "Sempai",
      render: (r) => localizeDemo(store.instructeurs.find((i) => i.id === r.assistant_id)?.nom ?? "—"),
    },
    {
      key: "phase",
      label: "Phase",
      render: (r) => <PhaseBadge phase={r.phase} />,
    },
    {
      key: "statut",
      label: locale === "en" ? "Status" : "Statut",
      render: (r) => <EntityStatusBadge statut={r.statut} />,
    },
  ];

  if (allowCrud) {
    return (
      <div data-testid={testId}>
        <p className="muted" data-testid="classes-demo-notice">
          {labels.notice}
        </p>
        <EntityCrudPanel
          collectionKey="classes"
          rows={store.classes}
          testId="admin-classes-crud"
          searchFields={["nom", "groupe", "id", "categorie"]}
          columns={columns}
          fields={[
            { name: "id", label: "ID", required: true },
            { name: "nom", label: locale === "en" ? "Name" : "Nom", required: true },
            {
              name: "dojo_id",
              label: "Dojo",
              required: true,
              options: dojoOptions,
            },
            { name: "groupe", label: locale === "en" ? "Group" : "Groupe", required: true },
            {
              name: "categorie",
              label: locale === "en" ? "Category" : "Catégorie",
              required: true,
              options: Object.entries(CLASS_CATEGORIES).map(([value, l]) => ({
                value,
                label: locale === "en" ? l.en : l.fr,
              })),
            },
            { name: "niveau", label: locale === "en" ? "Level" : "Niveau" },
            {
              name: "instructeur_id",
              label: "Sensei",
              options: [{ value: "", label: "—" }, ...instructeurOptions],
            },
            {
              name: "assistant_id",
              label: "Sempai",
              options: [{ value: "", label: "—" }, ...instructeurOptions],
            },
            {
              name: "jour",
              label: locale === "en" ? "Day" : "Jour",
              required: true,
              options: Object.entries(WEEKDAYS).map(([value, l]) => ({
                value,
                label: locale === "en" ? l.en : l.fr,
              })),
            },
            { name: "heure_debut", label: locale === "en" ? "Start" : "Début", required: true },
            { name: "heure_fin", label: locale === "en" ? "End" : "Fin", required: true },
            {
              name: "phase",
              label: "Phase",
              options: Object.entries(PHASE_LABELS).map(([value, l]) => ({
                value,
                label: locale === "en" ? l.en : l.fr,
              })),
            },
            {
              name: "statut",
              label: locale === "en" ? "Status" : "Statut",
              options: [
                { value: "actif", label: "Actif" },
                { value: "a_confirmer", label: "À confirmer" },
                { value: "inactif", label: "Inactif" },
              ],
            },
            { name: "notes", label: "Notes" },
          ]}
          buildItem={(form, existingId) => ({
            id: existingId || form.id.trim(),
            nom: form.nom.trim(),
            dojo_id: form.dojo_id,
            groupe: form.groupe.trim(),
            categorie: form.categorie,
            niveau: form.niveau.trim(),
            instructeur_id: form.instructeur_id || null,
            assistant_id: form.assistant_id || null,
            jour: form.jour,
            heure_debut: form.heure_debut.trim(),
            heure_fin: form.heure_fin.trim(),
            statut: form.statut || "actif",
            phase: form.phase || "phase1",
            notes: form.notes.trim(),
            demo: true,
          })}
        />
      </div>
    );
  }

  return (
    <div data-testid={testId}>
      <DataTable
        rows={rows}
        columns={columns}
        searchFields={["nom", "groupe", "categorie"]}
        filters={[
          {
            field: "phase",
            label: "Phase",
            options: Object.entries(PHASE_LABELS).map(([value, l]) => ({
              value,
              label: locale === "en" ? l.en : l.fr,
            })),
          },
          {
            field: "categorie",
            label: locale === "en" ? "Category" : "Catégorie",
            options: Object.entries(CLASS_CATEGORIES).map(([value, l]) => ({
              value,
              label: locale === "en" ? l.en : l.fr,
            })),
          },
        ]}
        emptyMessage={labels.empty}
        testId={`${testId}-table`}
      />
    </div>
  );
}

export function EventsListPanel({
  scope: scopeProp,
  allowCrud = false,
  testId = "karate-events-panel",
}) {
  const locale = useLocale();
  const { localizeDemo } = useKarateUi();
  const store = useDataStore();
  const scope = scopeProp || useScheduleScope();

  const rows = useMemo(() => {
    let list = store.events.filter((e) => e.statut !== "inactif");
    if (scope.mode === "sensei" || scope.mode === "sempai") {
      list = list.filter((e) => !scope.dojoId || e.dojo_id === scope.dojoId);
    } else if (scope.mode === "student" && scope.dojoId) {
      list = list.filter((e) => e.dojo_id === scope.dojoId);
    } else if (scope.mode === "parent" && scope.childIds?.length) {
      const children = store.etudiants.filter((e) => scope.childIds.includes(e.id));
      const dojoIds = new Set(children.map((c) => c.dojo_id));
      list = list.filter((e) => dojoIds.has(e.dojo_id));
    }
    return list;
  }, [store.events, store.etudiants, scope]);

  const columns = [
    {
      key: "titre",
      label: locale === "en" ? "Title" : "Titre",
      render: (r) => (
        <>
          {localizeDemo(r.titre)} {demoFlag(r)}
        </>
      ),
    },
    {
      key: "type",
      label: locale === "en" ? "Type" : "Type",
      render: (r) => labelFromMap(EVENT_TYPES, r.type, locale),
    },
    { key: "date", label: locale === "en" ? "Date" : "Date" },
    {
      key: "dojo_id",
      label: "Dojo",
      render: (r) => localizeDemo(store.dojos.find((d) => d.id === r.dojo_id)?.nom ?? r.dojo_id),
    },
    { key: "audience", label: locale === "en" ? "Audience" : "Public", render: (r) => localizeDemo(r.audience) },
    {
      key: "phase",
      label: "Phase",
      render: (r) => <PhaseBadge phase={r.phase} />,
    },
    {
      key: "statut",
      label: locale === "en" ? "Status" : "Statut",
      render: (r) => <EntityStatusBadge statut={r.statut} />,
    },
  ];

  if (allowCrud) {
    const dojoOptions = store.dojos.map((d) => ({ value: d.id, label: localizeDemo(d.nom) }));
    return (
      <div data-testid={testId}>
        <p className="muted" data-testid="events-demo-notice">
          {locale === "en"
            ? "Demo events — exams and competitions not operational."
            : "Événements démo — examens et compétitions non opérationnels."}
        </p>
        <EntityCrudPanel
          collectionKey="events"
          rows={store.events}
          testId="admin-events-crud"
          searchFields={["titre", "type", "id"]}
          columns={columns}
          fields={[
            { name: "id", label: "ID", required: true },
            { name: "titre", label: locale === "en" ? "Title" : "Titre", required: true },
            {
              name: "type",
              label: locale === "en" ? "Type" : "Type",
              required: true,
              options: Object.entries(EVENT_TYPES).map(([value, l]) => ({
                value,
                label: locale === "en" ? l.en : l.fr,
              })),
            },
            { name: "date", label: locale === "en" ? "Date" : "Date", type: "date", required: true },
            { name: "dojo_id", label: "Dojo", required: true, options: dojoOptions },
            { name: "audience", label: locale === "en" ? "Audience" : "Public" },
            {
              name: "visibilite",
              label: locale === "en" ? "Visibility" : "Visibilité",
              options: [
                { value: "public", label: "Public" },
                { value: "membres", label: "Membres" },
                { value: "admin", label: "Admin" },
              ],
            },
            {
              name: "phase",
              label: "Phase",
              options: Object.entries(PHASE_LABELS).map(([value, l]) => ({
                value,
                label: locale === "en" ? l.en : l.fr,
              })),
            },
            {
              name: "statut",
              label: locale === "en" ? "Status" : "Statut",
              options: [
                { value: "actif", label: "Actif" },
                { value: "a_confirmer", label: "À confirmer" },
                { value: "inactif", label: "Inactif" },
              ],
            },
            { name: "notes", label: "Notes" },
          ]}
          buildItem={(form, existingId) => ({
            id: existingId || form.id.trim(),
            type: form.type,
            titre: form.titre.trim(),
            date: form.date,
            dojo_id: form.dojo_id,
            audience: form.audience.trim(),
            visibilite: form.visibilite || "membres",
            statut: form.statut || "actif",
            phase: form.phase || "phase1",
            notes: form.notes.trim(),
            demo: true,
          })}
        />
      </div>
    );
  }

  return (
    <div data-testid={testId}>
      <DataTable
        rows={rows}
        columns={columns}
        searchFields={["titre", "type", "audience"]}
        filters={[
          {
            field: "type",
            label: locale === "en" ? "Type" : "Type",
            options: Object.entries(EVENT_TYPES).map(([value, l]) => ({
              value,
              label: locale === "en" ? l.en : l.fr,
            })),
          },
          {
            field: "phase",
            label: "Phase",
            options: Object.entries(PHASE_LABELS).map(([value, l]) => ({
              value,
              label: locale === "en" ? l.en : l.fr,
            })),
          },
        ]}
        emptyMessage={locale === "en" ? "No events found." : "Aucun événement trouvé."}
        testId={`${testId}-table`}
      />
    </div>
  );
}

export function LightAttendancePanel({
  scope: scopeProp,
  canEdit = true,
  testId = "karate-attendance-panel",
}) {
  const locale = useLocale();
  const store = useDataStore();
  const scope = scopeProp || useScheduleScope();
  const [classId, setClassId] = useState("");
  const [sessionDate, setSessionDate] = useState(DEMO_ATTENDANCE_DATE);

  const availableClasses = useMemo(() => {
    let list = store.classes.filter((c) => c.statut === "actif");
    if (scope.mode === "sensei") {
      list = list.filter((c) => c.instructeur_id === SENSEI_INSTRUCTOR_ID);
    } else if (scope.mode === "sempai") {
      list = list.filter((c) => c.assistant_id === SEMPAI_INSTRUCTOR_ID);
    } else if (scope.mode === "admin") {
      /* all */
    } else {
      list = [];
    }
    return list;
  }, [store.classes, scope]);

  const selectedClass = availableClasses.find((c) => c.id === classId) || availableClasses[0];
  const effectiveClassId = classId || selectedClass?.id || "";

  const students = useMemo(() => {
    if (!effectiveClassId) return [];
    const cls = store.classes.find((c) => c.id === effectiveClassId);
    if (!cls) return [];
    return store.etudiants.filter(
      (e) =>
        e.statut !== "inactif" &&
        e.dojo_id === cls.dojo_id &&
        (e.class_id === cls.id || e.groupe === cls.groupe),
    );
  }, [store.etudiants, store.classes, effectiveClassId]);

  const getStatus = (studentId) => {
    const rec = store.presences.find(
      (p) => p.class_id === effectiveClassId && p.student_id === studentId && p.date === sessionDate,
    );
    return rec?.statut ?? "non_suivi";
  };

  const handleStatusChange = (studentId, statut) => {
    if (!canEdit || scope.mode === "student" || scope.mode === "parent") return;
    upsertPresence({
      class_id: effectiveClassId,
      student_id: studentId,
      date: sessionDate,
      statut,
      note: "",
      demo: true,
    });
    notifyDataUpdate();
  };

  const statusOptions = Object.entries(ATTENDANCE_STATUSES).map(([value, l]) => ({
    value,
    label: locale === "en" ? l.en : l.fr,
  }));

  return (
    <GsdReadableBlock testId={testId}>
      <p className="muted" data-testid="attendance-phase1-notice">
        {locale === "en"
          ? "Light attendance Phase 1 — local mock only. Full module later."
          : "Suivi léger Phase 1 — mock local uniquement. Module complet plus tard."}{" "}
        <DemoBadge />
      </p>
      <div className="attendance-toolbar btn-row">
        <label>
          {locale === "en" ? "Class" : "Classe"}
          <select
            value={effectiveClassId}
            onChange={(e) => setClassId(e.target.value)}
            data-testid="attendance-class-select"
          >
            {availableClasses.map((c) => (
              <option key={c.id} value={c.id}>
                {c.nom}
              </option>
            ))}
          </select>
        </label>
        <label>
          {locale === "en" ? "Date" : "Date"}
          <input
            type="date"
            value={sessionDate}
            onChange={(e) => setSessionDate(e.target.value)}
            data-testid="attendance-date-input"
          />
        </label>
      </div>
      {students.length === 0 ? (
        <p className="muted" data-testid="attendance-empty">
          {locale === "en" ? "No demo students for this class." : "Aucun étudiant démo pour cette classe."}
        </p>
      ) : (
        <table className="gsd-admin-panel__table data-table" data-testid="attendance-students-table">
          <thead>
            <tr>
              <th>{locale === "en" ? "Student" : "Étudiant"}</th>
              <th>{locale === "en" ? "Group" : "Groupe"}</th>
              <th>{locale === "en" ? "Status" : "Statut"}</th>
            </tr>
          </thead>
          <tbody>
            {students.map((s) => (
              <tr key={s.id} data-testid={`attendance-row-${s.id}`}>
                <td>
                  {s.prenom} {s.nom} <DemoBadge />
                </td>
                <td>{s.groupe}</td>
                <td>
                  {canEdit && scope.mode !== "student" && scope.mode !== "parent" ? (
                    <select
                      value={getStatus(s.id)}
                      onChange={(e) => handleStatusChange(s.id, e.target.value)}
                      data-testid={`attendance-status-${s.id}`}
                      aria-label={locale === "en" ? "Attendance status" : "Statut présence"}
                    >
                      {statusOptions.map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <span data-testid={`attendance-readonly-${s.id}`}>
                      {labelFromMap(ATTENDANCE_STATUSES, getStatus(s.id), locale)}
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </GsdReadableBlock>
  );
}

export function StudentScheduleSummary({ testId = "student-schedule-summary" }) {
  const locale = useLocale();
  const { localizeDemo } = useKarateUi();
  const scope = useScheduleScope();
  const store = useDataStore();
  const student = store.etudiants.find((e) => e.id === scope.studentId);
  const classes = store.classes.filter(
    (c) =>
      c.id === student?.class_id ||
      (student && c.dojo_id === student.dojo_id && c.groupe === student.groupe),
  );
  const dojo = store.dojos.find((d) => d.id === student?.dojo_id);
  const sensei = dojo?.sensei_id ? store.instructeurs.find((i) => i.id === dojo.sensei_id) : null;

  return (
    <GsdReadableBlock testId={testId}>
      <p className="muted">
        {locale === "en" ? "Phase 1 demo schedule." : "Horaire démo Phase 1."} <DemoBadge />
      </p>
      {classes.map((c) => (
        <div key={c.id} className="schedule-card" data-testid={`student-class-${c.id}`}>
          <h3>{localizeDemo(c.nom)}</h3>
          <p>
            {labelFromMap(WEEKDAYS, c.jour, locale)} · {c.heure_debut} – {c.heure_fin}
          </p>
          <p>
            {locale === "en" ? "Dojo" : "Dojo"} : {localizeDemo(dojo?.nom ?? "—")} · Sensei : {localizeDemo(sensei?.nom ?? "—")}
          </p>
          <PhaseBadge phase={c.phase} />
        </div>
      ))}
    </GsdReadableBlock>
  );
}

export function ParentScheduleSummary({ testId = "parent-schedule-summary" }) {
  const locale = useLocale();
  const { localizeDemo } = useKarateUi();
  const scope = useScheduleScope();
  const store = useDataStore();
  const children = store.etudiants.filter((e) => scope.childIds?.includes(e.id));

  return (
    <GsdReadableBlock testId={testId}>
      <p className="muted" data-testid="parent-no-payments-notice">
        {locale === "en"
          ? "Payments not managed in Phase 1."
          : "Les paiements ne sont pas encore gérés en Phase 1."}
      </p>
      {children.map((child) => {
        const classes = store.classes.filter(
          (c) =>
            c.id === child.class_id ||
            (c.dojo_id === child.dojo_id && c.groupe === child.groupe),
        );
        return (
          <div key={child.id} data-testid={`parent-child-schedule-${child.id}`}>
            <h3>
              {localizeDemo(child.prenom)} {localizeDemo(child.nom)} <DemoBadge />
            </h3>
            {classes.length === 0 ? (
              <p className="muted">{locale === "en" ? "No class linked." : "Aucun cours lié."}</p>
            ) : (
              classes.map((c) => (
                <p key={c.id}>
                  {localizeDemo(c.nom)} — {labelFromMap(WEEKDAYS, c.jour, locale)} {c.heure_debut}–{c.heure_fin}
                </p>
              ))
            )}
          </div>
        );
      })}
    </GsdReadableBlock>
  );
}
